import { merge, getValueByPath, blankIfUndefined } from './helpers.js';

let config = {
  iconPack: 'mdi',
  useHtml5Validation: true,
  statusIcon: true
};
const setOptions = options => {
  config = options;
};
let VueInstance;
const setVueInstance = Vue => {
  VueInstance = Vue;
};
const Programmatic = {
  getOptions() {
    return config;
  },

  setOptions(options) {
    setOptions(merge(config, options, true));
  }

};
const Plugin = {
  install(Vue, options = {}) {
    setVueInstance(Vue); // Options

    setOptions(merge(config, options, true));
  }

};

var BaseComponentMixin = {
  methods: {
    computedClass(component, field, defaultValue) {
      const override = getValueByPath(config, `${component}.override`, false);
      const globalClazz = getValueByPath(config, `${component}.${field}`, '');
      const currrentClazz = this.$props[field];
      return `${override ? '' : defaultValue} ${blankIfUndefined(globalClazz)} ${blankIfUndefined(currrentClazz)}`.trim();
    }

  }
};

const isVue2 = instance => {
  const vm = instance || VueInstance;
  return !vm || vm && vm.version.indexOf('2.') === 0;
};
const use = (vm, plugin) => {
  vm.use(plugin);
};
const component = (vm, component) => {
  vm.component(component.name, component);
};
const setGlobalProperty = (vm, property, value) => {
  if (isVue2()) {
    vm.prototype[property] = value;
  } else {
    vm.config.globalProperties[property] = value;
  }
};
const getGlobalProperty = (vm, property) => {
  return isVue2() ? vm.prototype[property] : vm.config.globalProperties[property];
};
const getListeners = cmp => {
  return isVue2() ? cmp.$listeners : cmp.$attrs;
};
const existsSlot = (cmp, name, scoped = false) => {
  if (isVue2()) {
    return scoped ? !!cmp.$scopedSlots[name] : !!cmp.$slots[name];
  }

  return cmp.$slots[name];
};
const getSlotInstance = (cmp, name, scoped = false, props = undefined) => {
  if (isVue2()) {
    return scoped ? cmp.$scopedSlots[name](props) : cmp.$slots[name];
  }

  return scoped ? cmp.$slots[name](props) : cmp.$slots[name]();
};
const setScopedSlot = (cmp, name, data) => {
  if (isVue2()) {
    cmp.$scopedSlots = {
      [name]: data
    };
  } else {
    cmp.$slots = {
      [name]: data
    };
  }
};
const createElement = (vue, tag, data, children) => {
  if (isVue2()) {
    const h = vue;
    return h(tag, data, children);
  } else {
    const {
      h,
      withDirectives
    } = vue;

    if (!data) {
      return h(resolveTag(tag, vue), data, wrapChildren(tag, children));
    }

    const vnode = h(resolveTag(tag, vue), convertData(data), wrapChildren(tag, children));
    return data.directives ? withDirectives(vnode, resolveDirectives(data.directives, vue)) : vnode;
  }
};

const resolveTag = (tag, vue) => {
  const {
    Transition
  } = vue;
  return tag === 'transition' ? Transition : tag;
};

const resolveDirectives = (directives, vue) => {
  const {
    resolveDirective,
    vShow
  } = vue;
  return [...directives.map(d => [d.name === 'show' ? vShow : resolveDirective(d.name), d.value])];
};

const convertData = data => {
  const events = {};

  if (data.on) {
    Object.keys(data.on).map(k => {
      // add 'on' prefix and capitalize
      const eventName = `on${k.substring(0, 1)}${k.substring(1)}`;
      return events[eventName] = data.on[k];
    });
  }

  return {
    class: [...(data.class || []), data.staticClass],
    style: data.style,
    key: data.key,
    ref: data.ref,
    ...(data.props || {}),
    ...(data.attrs || {}),
    ...(data.domProps || {}),
    ...(events || {})
  };
};

const wrapChildren = (tag, children) => {
  return tag === 'transition' ? () => children : children;
};

var VueComponentMixin = ((params = {}) => {
  const vModel = params.vModel;
  const mixin = {
    props: {
      /** @model */
      value: vModel,

      /** @ignore */
      modelValue: vModel
    },

    data() {
      return {
        vueReady: false
      };
    },

    computed: {
      listeners() {
        return getListeners(this);
      },

      modelName() {
        return isVue2() ? 'value' : 'modelValue';
      }

    },
    methods: {
      existsSlot(name, scoped) {
        return existsSlot(this, name, scoped);
      },

      getSlotInstance(name, scoped, props) {
        return getSlotInstance(this, name, scoped, props);
      },

      setScopedSlot(name, value) {
        setScopedSlot(this, name, value);
      },

      emitModel(value) {
        if (isVue2()) {
          this.$emit('input', value);
        } else {
          this.$emit('update:modelValue', value);
        }
      },

      getModel() {
        return isVue2() ? this.value : this.modelValue;
      }

    },

    created() {
      // v-model watch, onModelChange methods as convetion
      if (vModel && typeof this.onModelChange !== 'undefined') {
        this.$watch(this.modelName, this.onModelChange);
      } // set ready when vue module has been loaded


      if (isVue2()) {
        this.vueReady = true;
      } else {
        import('vue').then(module => {
          this.$nextTick = module['nextTick'];

          this.$createElement = (tag, data, children) => createElement(module, tag, data, children);

          this.vueReady = true;
        });
      }
    },

    // Vue 3: beforeDestroy -> beforeUnmount
    beforeUnmount() {
      if (typeof this.$options.beforeDestroy !== 'undefined') {
        this.$options.beforeDestroy.apply(this);
      }
    }

  };
  return mixin;
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier
/* server only */
, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  } // Vue.extend constructor export interop.


  const options = typeof script === 'function' ? script.options : script; // render functions

  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true; // functional template

    if (isFunctionalTemplate) {
      options.functional = true;
    }
  } // scopedId


  if (scopeId) {
    options._scopeId = scopeId;
  }

  let hook;

  if (moduleIdentifier) {
    // server build
    hook = function (context) {
      // 2.3 injection
      context = context || // cached call
      this.$vnode && this.$vnode.ssrContext || // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true

      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      } // inject component styles


      if (style) {
        style.call(this, createInjectorSSR(context));
      } // register component module identifier for async chunk inference


      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    }; // used by ssr in case component is cached and beforeCreate
    // never gets called


    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function (context) {
      style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }

  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      const originalRender = options.render;

      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      const existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }

  return script;
}

const use$1 = plugin => {
  if (typeof window !== 'undefined' && window.Vue) {
    use(window.Vue, plugin);
  }
};
const registerPlugin = (vm, plugin) => {
  use(vm, plugin);
};
const registerComponent = (vm, component$1) => {
  component(vm, component$1);
};
const registerComponentProgrammatic = (vm, property, component) => {
  let oruga = getGlobalProperty(vm, '$oruga');
  if (!oruga) oruga = {};
  oruga[property] = component;
  setGlobalProperty(vm, '$oruga', oruga);
};

export { BaseComponentMixin as B, Programmatic as P, VueInstance as V, setOptions as a, registerComponentProgrammatic as b, config as c, Plugin as d, registerComponent as e, VueComponentMixin as f, getSlotInstance as g, isVue2 as i, normalizeComponent as n, registerPlugin as r, setVueInstance as s, use$1 as u };
